import { Typography, LinearProgress, Card, Skeleton, IconButton, InputBase } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Box } from '@mui/system';
import { linearProgressClasses } from '@mui/material/LinearProgress';
import { useInView } from "react-intersection-observer";
import { useState, useEffect } from 'react';
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import SearchIcon from '@mui/icons-material/Search';
import MapCard from './MapCard';
import ISO from "./iso.json";
import MapDialog from './MapDialog';
import ifEmoji from 'if-emoji';

const MapSectionBox = styled(({ itemRef, ...rest }) => (
    <Box ref={itemRef} component="section" {...rest} />
))(({ theme }) => ({
    display: "flex",
    flexDirection: "column",
    margin: theme.spacing(3, 0),
    [theme.breakpoints.up("lg")]: {
        flexDirection: "row",
        justifyContent: "space-between",
    },
}));

const MapDetail = styled(Box)(
    ({ theme }) => ({
        [theme.breakpoints.up("lg")]: {
            width: "48%",
        },
    })
);

function getFlagEmoji(city) {
    if (!(city in ISO)) {
        console.log(city);
        return "";
    }
    const codePoints = ISO[city].toUpperCase().split("").map((char) => 127397 + char.charCodeAt(0));
    return String.fromCodePoint(...codePoints);
};

const MapTitle = styled((props) => (
    <Typography variant="h2" {...props} />
))(({ theme }) => ({
    fontFamily: "Roboto Condensed",
    fontSize: "3.7rem",
    fontWeight: 700,
    textTransform: "capitalize",
}));

const MapSubtitle = styled(Typography)(
    ({ theme }) => ({
        fontFamily: "inter",
        fontSize: "1.6rem",
        fontWeight: 500,
        color: "#555",
    })
);

const ProgressContainer = styled(Box)(
    ({ theme }) => ({
        display: "flex",
        alignItems: "center",
        margin: theme.spacing(1, 0),
    })
);

const ProgressBar = styled(LinearProgress)(
    ({ theme }) => ({
        display: "block",
        width: "100%",
        height: 15,
        borderRadius: 10,
        marginRight: theme.spacing(1),
        [`&.${linearProgressClasses.colorPrimary}`]: {
            backgroundColor: theme.palette.grey[300],
        },
        [`& .${linearProgressClasses.bar}`]: {
            borderRadius: 10,
            backgroundColor: theme.palette.primary.light,
        },
    })
);

const ProgressCaption = styled(Typography)(
    ({ theme }) => ({
        fontSize: 11,
        fontWeight: "bold",
        marginTop: 0.5,
        whiteSpace: "nowrap",
    })
);

const ListCard = styled((props) => (
    <Card elevation={5} {...props} />
))(({ theme }) => ({
    display: "flex",
    flexDirection: "column",
    flexWrap: "wrap",
    overflowX: "auto",
    overflowY: "hidden",
    marginTop: theme.spacing(2),
    height: "19rem",
    padding: theme.spacing("1rem", 1),
    marginBottom: 16, // temp
    [theme.breakpoints.up("lg")]: {
        height: "38rem",
    },
}));

const ListItem = styled(Box)(
    ({ theme }) => ({
        backgroundColor: "#eee",
        height: "100%",
        width: "15.5rem",
        margin: theme.spacing(1.5, 1, 0.5, 1),
        padding: theme.spacing(1, 0.5, 1, 1.5),
        borderRadius: "1.5rem",
        transition: theme.transitions.create(["transform", "box-shadow"]),
        cursor: "pointer",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        "&:hover": {
            transform: `translateY(-${theme.spacing(1)})`,
            backgroundColor: "#dadada",
            boxShadow: "0px 7px purple",
        },
        [theme.breakpoints.up("lg")]: {
            height: `calc(50% - ${theme.spacing(2)})`,
        },
    })
);

const ItemFilterBox = styled(Box, { shouldForwardProp: (prop) => prop !== "focus" })(
    ({ theme, focus }) => ({
        width: "4.8rem",
        display: "flex",
        justifyContent: "flex-end",
        backgroundColor: "#fff",
        borderRadius: "10rem",
        transition: theme.transitions.create(["width", "background-color"]),
        ":hover": {
            width: "40%",
            backgroundColor: "#eee",
        },
        ...(focus && {
            width: "40%",
            backgroundColor: "#eee",
        }),
    })
);

const debounce = (func) => {
    let timer;
    return function (...args) {
        const context = this;
        if (timer) clearTimeout(timer);
        timer = setTimeout(() => {
            timer = null;
            func.apply(context, args);
        }, 500);
    };
};

const ItemFilter = ({ setQuery }) => {
    const [focus, setFocus] = useState(false);

    const handleChange = debounce((event) => {
        setQuery(event.target.value);
    });

    return (
        <ItemFilterBox focus={focus}>
            <InputBase
                onFocus={() => setFocus(true)}
                onBlur={() => setFocus(false)}
                placeholder="City Filter"
                onChange={handleChange}
                sx={{
                    width: "100%",
                    paddingLeft: 2.5,
                }}
            />
            <IconButton size="large" disableRipple>
                <SearchIcon />
            </IconButton>
        </ItemFilterBox>
    );
}

export default function MapSection({ filter, continent, count, dist, city_count, id }) {
    const [markers, setMarkers] = useState(null);
    const [selected, setSelected] = useState(null);
    const [query, setQuery] = useState("");

    let filteredMarkers = markers;

    if (filteredMarkers && query !== "") {
        filteredMarkers = filteredMarkers.filter(({ city }) => city.toLowerCase().includes(query.toLowerCase()));
    };

    const { ref, inView } = useInView({
        triggerOnce: true,
        initialInView: id === "#continent0",
    });

    useEffect(() => {
        const fetchMarkers = () => (
            fetch(`https://raw.githubusercontent.com/ruh010/locedge-db/master/${filter}/${continent}.json`)
                .then((resp) => resp.json())
                .then((data) => setMarkers(data))
        );
        inView && fetchMarkers();
        return () => setMarkers(null);
    }, [filter, continent, inView]);

    const handleClickItem = (event) => {
        const itemIdx = parseInt(event.currentTarget.id.split("-")[1]);
        setSelected(itemIdx);
    };

    return (
        <MapSectionBox itemRef={ref} id={id}>
            {filteredMarkers && selected !== null && (
                <MapDialog
                    onClose={() => setSelected(null)}
                    value={filteredMarkers[selected]}
                />
            )}
            {inView && (
                <MapDetail>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <Box sx={{ flexShrink: 0, marginRight: 1 }}>
                            <MapTitle>{continent.replace("-", " ")}</MapTitle>
                            <MapSubtitle>with {count} identified PoPs in {city_count} cities.</MapSubtitle>
                        </Box>
                        <ItemFilter setQuery={setQuery} />
                    </Box>
                    <ProgressContainer>
                        <ProgressBar variant="determinate" value={dist} />
                        <ProgressCaption>{dist}% of All PoPs</ProgressCaption>
                    </ProgressContainer>
                    <ListCard>
                        {filteredMarkers ? filteredMarkers.map(({ city, cdn }, idx) => {
                            let emoji = getFlagEmoji(city);
                            if (!ifEmoji(emoji)) emoji = null;
                            return (
                                <ListItem
                                    key={city}
                                    id={`list-${idx}`}
                                    onClick={handleClickItem}
                                >
                                    <Box sx={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        // alignItems: "center",
                                        marginRight: 0.5,
                                    }}>
                                        <Typography sx={{
                                            fontFamily: "montserrat",
                                            fontWeight: 500,
                                        }}>
                                            <b>{cdn.length}</b> CDNs
                                        </Typography>
                                        {emoji ? (
                                            <Typography sx={{
                                                fontSize: "2.5rem",
                                                marginTop: -0.9,
                                            }}>
                                                {emoji}
                                            </Typography>
                                        ) : (
                                            <img
                                                src={`https://flagcdn.com/48x36/${ISO[city].toLowerCase()}.png`}
                                                width={24}
                                                height={18}
                                                alt={ISO[city]}
                                                loaing="lazy"
                                            />
                                        )}
                                    </Box>
                                    <Typography sx={{
                                        textTransform: "capitalize",
                                        fontFamily: "montserrat",
                                        fontWeight: 800,
                                        fontSize: "2rem",
                                        whiteSpace: "nowrap",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                    }}>
                                        {city}
                                    </Typography>
                                    <Box sx={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                        marginRight: 0.5,
                                        color: "#1264A3"
                                    }}>
                                        <Typography sx={{
                                            display: "inline-block",
                                            fontSize: "1.5rem",
                                            fontFamily: "inter",
                                            fontWeight: 500,
                                        }}>
                                            GeoHints
                                        </Typography>
                                        <ArrowForwardIcon sx={{ fontSize: "2rem" }} />
                                    </Box>
                                </ListItem>
                            )
                        }) : (
                            <Skeleton variant="rectangular" sx={{
                                width: "100%",
                                height: { xs: "17rem", lg: "36rem" }
                            }} />
                        )}
                    </ListCard>
                </MapDetail>
            )}
            <MapCard
                markers={filteredMarkers}
                continent={continent}
                onClick={handleClickItem}
            />
        </MapSectionBox>
    );
};
